import React, {useMemo, useState, useEffect, type FC, type CSSProperties} from 'react';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import {type ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';
import {
  type IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import type {IMediaItem} from '../../../../types/app-types';
import {Alignment} from '../../../../constants';
import {Swiper, type SwiperClass, SwiperSlide} from 'swiper/react';
import {Navigation, A11y} from 'swiper/modules';
import s from './ProductMediaSwiper.scss';
import {ArrowButton} from './ArrowButton/ArrowButton';
import classNames from 'classnames';
import {useProductGallery} from '../../ProductGalleryProvider/ProductGalleryProvider';
import {getArrowDirection} from './utils/getArrowDirection';
import {useSwiperNavigation} from './utils/hooks/useSwiperNavigation';
import {
  NEXT_ARROW_BUTTON_ID,
  PREV_ARROW_BUTTON_ID,
  SWIPER_SPEED,
  THUMBNAIL_SIZE,
  THUMBNAIL_GAP,
  ARROW_BUTTON_SIZE,
  THUMBNAIL_BORDER_WIDTH,
} from './utils/constants';
import {Thumbnail} from './Thumbnail/Thumbnail';
import {useHasVisibleArrows} from './utils/hooks/useHasVisibleArrows';

export enum ProductMediaSwiperDataHooks {
  Root = 'product-media-swiper-root',
  PrevArrow = 'product-media-swiper-prev-arrow',
  NextArrow = 'product-media-swiper-next-arrow',
}
export interface ProductMediaSwiperProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  media: IMediaItem[];
  isVertical: boolean;
  align: Alignment;
}

const ProductMediaSwiperComponent: FC<ProductMediaSwiperProps> = ({media, isVertical, align, t}) => {
  const {changeSelectedIndex} = useProductGallery();
  const [swiper, setSwiper] = useState<SwiperClass>(null);
  const {prevNavigation, nextNavigation, handleKeyDown} = useSwiperNavigation(media.length, swiper, isVertical);
  const hasVisibleArrows = useHasVisibleArrows(swiper);
  const {isRTL} = useEnvironment();

  const swiperRootClasses = useMemo(
    () =>
      classNames(s.swiperRoot, {
        [s.swiperRootHorizontal]: !isVertical,
        [s.swiperRootVertical]: isVertical,
      }),
    [isVertical]
  );

  const swiperWrapperClasses = useMemo(() => {
    const isHorizontalWithoutArrows = !isVertical && !hasVisibleArrows;
    const shouldCenterAlign = isHorizontalWithoutArrows && align === Alignment.CENTER;
    const shouldStartAlign = isHorizontalWithoutArrows && align === Alignment.START;
    return classNames(s.swiperWrapper, {
      [s.swiperWrapperCenterAlignment]: shouldCenterAlign,
      [s.swiperWrapperStartAlignment]: shouldStartAlign,
      [s.swiperWrapperVerticalSSR]: isVertical,
    });
  }, [isVertical, hasVisibleArrows, align]);

  /* istanbul ignore next */
  useEffect(() => {
    if (!swiper?.wrapperEl) {
      return;
    }
    setTimeout(() => swiper?.wrapperEl?.classList.remove(s.swiperWrapperVerticalSSR), 0);
  }, [swiper]);

  const swiperSlideClasses = useMemo(() => {
    return classNames(s.swiperSlide, s.swiperSlideThumbnail, {
      [s.swiperSlideThumbnailHorizontal]: !isVertical,
      [s.swiperSlideThumbnailVertical]: isVertical,
    });
  }, [isVertical]);

  const singleThumbnailSize = THUMBNAIL_SIZE + THUMBNAIL_GAP + THUMBNAIL_BORDER_WIDTH * 2;
  const totalThumbnailsSizeNeeded = media.length * singleThumbnailSize;
  const inlineCssVars = {
    '--total-swiper-size-needed': `${totalThumbnailsSizeNeeded}px`,
    '--thumbnail-gap': `${THUMBNAIL_GAP}px`,
    '--thumbnail-border-width': `${THUMBNAIL_BORDER_WIDTH}px`,
    '--arrow-button-size': `${ARROW_BUTTON_SIZE}px`,
  };

  return (
    <div
      data-hook={ProductMediaSwiperDataHooks.Root}
      style={inlineCssVars as CSSProperties}
      className={classNames(s.root, {[s.rootVertical]: isVertical})}
      {...(hasVisibleArrows && {onKeyDown: handleKeyDown})}>
      <ArrowButton
        id={PREV_ARROW_BUTTON_ID}
        dataHook={ProductMediaSwiperDataHooks.PrevArrow}
        onClick={prevNavigation}
        isVertical={isVertical}
        direction={getArrowDirection(true, isVertical, isRTL)}
      />
      <Swiper
        onSwiper={setSwiper}
        speed={SWIPER_SPEED}
        direction={isVertical ? 'vertical' : 'horizontal'}
        className={swiperRootClasses}
        wrapperClass={swiperWrapperClasses}
        modules={[Navigation, A11y]}
        navigation={{prevEl: `#${PREV_ARROW_BUTTON_ID}`, nextEl: `#${NEXT_ARROW_BUTTON_ID}`}}
        a11y={{
          prevSlideMessage: t('productPage.slideGallery.previous.ariaLabel'),
          nextSlideMessage: t('productPage.slideGallery.next.ariaLabel'),
        }}
        loop={true}
        slidesPerView="auto"
        watchSlidesProgress
        allowTouchMove={false}
        spaceBetween={0}>
        {media.map((mediaItem, index) => (
          <SwiperSlide className={swiperSlideClasses} key={index} onClick={() => changeSelectedIndex(index)}>
            <Thumbnail mediaItem={mediaItem} index={index} />
          </SwiperSlide>
        ))}
      </Swiper>
      <ArrowButton
        id={NEXT_ARROW_BUTTON_ID}
        dataHook={ProductMediaSwiperDataHooks.NextArrow}
        onClick={nextNavigation}
        isVertical={isVertical}
        direction={getArrowDirection(false, isVertical, isRTL)}
      />
    </div>
  );
};

export const ProductMediaSwiper = withGlobalProps(withTranslations('globals.texts')(ProductMediaSwiperComponent));
